import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Briefcase, Coins, Landmark, Sword, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import StarfieldCanvas from "@/components/StarfieldCanvas";
import HeroGlobe from "@/components/HeroGlobe";
import { TERRITORIA } from "@/data/content";

const META = [
  ["STATUS", TERRITORIA.status],
  ["ENGINE", TERRITORIA.engine],
  ["GENRE", TERRITORIA.genre],
  ["TARGET", TERRITORIA.target],
];

const CAREER_ICONS = { SOLDIER: Sword, ENTREPRENEUR: Briefcase, TRADER: Coins, POLITICIAN: Landmark };

const useCountdown = (target) => {
  const calc = () => {
    const diff = Math.max(0, new Date(target).getTime() - Date.now());
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor(diff / 3600000) % 24,
      m: Math.floor(diff / 60000) % 60,
      s: Math.floor(diff / 1000) % 60,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
};

const Countdown = () => {
  const t = useCountdown(TERRITORIA.betaDate);
  const units = [
    [t.d, "DAYS"],
    [t.h, "HRS"],
    [t.m, "MIN"],
    [t.s, "SEC"],
  ];
  return (
    <div data-testid="beta-countdown" className="border border-[#282D37] bg-[#0D0F12] px-6 py-6 sm:px-10">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
            <span className="font-mono text-[11px] tracking-[0.35em] text-[#E52E2E]">OPEN BETA LAUNCH</span>
          </div>
          <p className="mt-2 font-mono text-xs tracking-[0.25em] text-[#5F6573]">01 / 01 / 2027 · 12:00 UTC</p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3">
          {units.map(([v, label], i) => (
            <div key={label} className="flex items-center gap-1.5 sm:gap-3">
              <div className="flex min-w-[56px] flex-col items-center border border-[#282D37] bg-[#0A0B0D] px-1.5 py-2.5 sm:min-w-[64px] sm:px-2">
                <span className="font-display text-2xl font-bold leading-none text-[#F4F5F7] tabular-nums sm:text-3xl">
                  {String(v).padStart(2, "0")}
                </span>
                <span className="mt-1.5 font-mono text-[9px] tracking-[0.25em] text-[#5F6573]">{label}</span>
              </div>
              {i < units.length - 1 && <span className="font-display text-xl text-[#E52E2E]/60">:</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Lightbox = ({ index, onClose }) => {
  const item = TERRITORIA.gallery[index];
  return (
    <motion.div
      data-testid="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      className="fixed inset-0 z-[90] flex cursor-zoom-out items-center justify-center bg-[#0A0B0D]/95 p-6 backdrop-blur-md"
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute right-6 top-6 text-[#9EA4B0] transition-colors hover:text-[#E52E2E]"
        aria-label="Close"
      >
        <X size={28} />
      </button>
      <motion.figure
        initial={{ scale: 0.92, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-5xl"
      >
        <img src={item.src} alt={item.caption} className="max-h-[78vh] w-full border border-[#282D37] object-contain" />
        <figcaption className="mt-4 text-center font-mono text-xs tracking-[0.3em] text-[#9EA4B0]">
          {item.caption.toUpperCase()}
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

const GameDetail = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div>
      <section data-testid="game-hero" className="relative flex min-h-[85vh] items-end overflow-hidden bg-[#05070C]">
        <StarfieldCanvas density={170} className="absolute inset-0 h-full w-full" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 50% at 72% 40%, rgba(58,100,190,0.14) 0%, transparent 65%), radial-gradient(ellipse 45% 40% at 20% 70%, rgba(229,46,46,0.07) 0%, transparent 70%), linear-gradient(to top, #0A0B0D 2%, transparent 45%)",
          }}
        />
        <EmberCanvas density={10} className="absolute inset-0 h-full w-full opacity-60" />
        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 pb-16 pt-28 [&>*]:min-w-0 lg:grid-cols-[1.05fr_0.95fr] lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/games"
              data-testid="game-back-link"
              className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
            >
              <ArrowLeft size={14} /> THE VAULT
            </Link>
            <div className="mt-8 flex items-center gap-3">
              <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
              <span className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">{TERRITORIA.status}</span>
            </div>
            <h1 className="mt-4 font-display text-7xl font-black uppercase leading-none tracking-tight text-white sm:text-8xl lg:text-9xl">
              {TERRITORIA.title}
            </h1>
            <p className="mt-6 max-w-2xl font-display text-xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-2xl">
              {TERRITORIA.tagline}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="game-globe"
            className="flex min-w-0 justify-center"
          >
            <HeroGlobe />
          </motion.div>
        </div>
      </section>

      <section className="border-y border-[#282D37] bg-[#0D0F12]">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px lg:grid-cols-4">
          {META.map(([k, v], i) => (
            <Reveal key={k} delay={0.08 * i}>
              <div data-testid={`game-meta-${k.toLowerCase()}`} className="px-6 py-8 text-center lg:px-8">
                <p className="font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">{k}</p>
                <p className={`mt-2 font-mono text-xs font-bold tracking-widest sm:text-sm ${k === "STATUS" ? "text-[#E52E2E]" : "text-[#F4F5F7]"}`}>
                  {v}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pt-16 lg:px-10">
        <Reveal>
          <Countdown />
        </Reveal>
      </section>

      <section data-testid="game-overview" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.6fr] lg:gap-24">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">OVERVIEW // THE WORLD</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
              A world with no resets
            </h2>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-[#9EA4B0] sm:text-lg">{TERRITORIA.synopsis}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <blockquote className="mt-10 border-l-2 border-[#E52E2E] pl-6 font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#F4F5F7] sm:text-3xl">
                "Every border, law, coalition, election and war is shaped by players."
              </blockquote>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="mt-10 text-base leading-relaxed text-[#9EA4B0]">
                A pioneer server is already live — the first citizens are forging nations, economies
                and front lines in the open, build by build. This is not a world you visit. It is a
                world you govern, supply, defend and lose.
              </p>
            </Reveal>
            <Reveal delay={0.34}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={TERRITORIA.links.world}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="game-link-world"
                  className="group flex items-center gap-2 bg-[#E52E2E] px-7 py-3.5 font-mono text-xs font-bold tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#FF3B3B]"
                >
                  ENTER THE WORLD <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={TERRITORIA.links.codex}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="game-link-codex"
                  className="group flex items-center gap-2 border border-[#3A404E] px-7 py-3.5 font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
                >
                  EXPLORE THE CODEX <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="game-video" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 py-24 lg:grid-cols-[1fr_1.6fr] lg:gap-16 lg:px-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">DEV BUILD // LIVE FEED</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
              History, written in real time
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
              Raw, unfiltered footage from the Territoria engine on the pioneer server. What you are
              watching is a world being built by its first citizens — not a trailer.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-px border border-[#282D37] bg-[#282D37]">
              {TERRITORIA.buildStats.map(([k, v]) => (
                <div key={k} className="bg-[#121418] px-5 py-4">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">{k}</p>
                  <p className="mt-1 font-mono text-xs font-bold tracking-widest text-[#F4F5F7]">{v}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#282D37] bg-[#0A0B0D]">
              <div className="flex items-center justify-between border-b border-[#282D37] px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
                  <span className="font-mono text-[10px] tracking-[0.25em] text-[#9EA4B0]">
                    REC · TERRITORIA_ENGINE_V0.0.007
                  </span>
                </div>
                <span className="hidden font-mono text-[10px] tracking-[0.2em] text-[#5F6573] sm:block">LIVE SIGNAL</span>
              </div>
              <video
                data-testid="game-dev-video"
                src={TERRITORIA.video}
                className="aspect-video w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="game-careers" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">CAREER PATHS // BUILDS</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Choose your path
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TERRITORIA.careers.map((c, i) => {
            const Icon = CAREER_ICONS[c.code];
            return (
              <Reveal key={c.code} delay={0.08 * i}>
                <div
                  data-testid={`career-${c.code.toLowerCase()}`}
                  className="group steel-panel h-full p-7 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]"
                >
                  <Icon size={26} className="text-[#E52E2E]" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                    {c.code}
                  </h3>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-[#E52E2E]">{c.motto.toUpperCase()}</p>
                  <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{c.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="game-pillars" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">KEY PILLARS // SYSTEMS</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TERRITORIA.pillars.map((p, i) => (
              <Reveal key={p.code} delay={0.1 * i}>
                <div className="group steel-panel h-full p-8 transition-[border-color] duration-500 hover:border-[#E52E2E]/50">
                  <p className="font-mono text-sm tracking-[0.3em] text-[#E52E2E]">{p.code}</p>
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-2xl">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="game-gallery" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">VISUAL GALLERY // THE WORLD</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            From the frontier
          </h2>
          <p className="mt-4 font-mono text-[11px] tracking-[0.25em] text-[#5F6573]">
            IN-GAME CAPTURES — PIONEER SERVER BUILD V0.0.007
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {TERRITORIA.gallery.map((g, i) => (
            <Reveal key={g.caption} delay={0.08 * i}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(i)}
                className="group relative block w-full cursor-zoom-in overflow-hidden border border-[#282D37]"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  className="aspect-[16/10] w-full object-cover opacity-80 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-5 text-left">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">{g.caption.toUpperCase()}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="game-roadmap" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-8">
              <div>
                <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">DEVELOPMENT ROADMAP</p>
                <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
                  The road to open beta
                </h2>
                <p className="mt-4 font-mono text-xs tracking-[0.25em] text-[#9EA4B0]">
                  7 PHASES · 42 KEY AREAS · <span className="text-[#E52E2E]">19 SHIPPED</span> · 6 IN BUILD
                </p>
              </div>
              <div className="w-full max-w-sm">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.3em]">
                  <span className="text-[#5F6573]">OVERALL</span>
                  <span className="text-[#E52E2E]">52%</span>
                </div>
                <div className="mt-2 h-1.5 w-full border border-[#282D37] bg-[#0A0B0D]">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "52%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full bg-[#E52E2E] shadow-[0_0_16px_rgba(229,46,46,0.5)]"
                  />
                </div>
              </div>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-px border border-[#282D37] bg-[#282D37] sm:grid-cols-2 lg:grid-cols-7">
            {TERRITORIA.roadmap.map((p, i) => (
              <Reveal key={p.phase} delay={0.06 * i}>
                <div data-testid={`roadmap-${i + 1}`} className="h-full bg-[#0D0F12] p-6">
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`h-2.5 w-2.5 rotate-45 border ${
                        p.done ? "border-[#E52E2E] bg-[#E52E2E]" : "border-[#3A404E] bg-transparent"
                      }`}
                    />
                    <span className={`font-mono text-[10px] tracking-[0.25em] ${p.done ? "text-[#E52E2E]" : "text-[#5F6573]"}`}>
                      {p.phase}
                    </span>
                  </div>
                  <p className={`mt-3 font-display text-base font-bold uppercase leading-tight tracking-wide ${p.done ? "text-[#F4F5F7]" : "text-[#9EA4B0]"}`}>
                    {p.label}
                  </p>
                  <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-[#5F6573]">
                    {p.done ? "SHIPPED" : "IN BUILD"}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && <Lightbox index={lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default GameDetail;
